import moment from 'moment';
import momentTz, { Moment as TimeZoneMoment } from 'moment-timezone';
import { getPreciseDealPricingStatus } from '../../../helpers/issue/deal-status';
import { IssueOrderPeriod } from '../../../helpers/issue/order-period';
import {
  BasicDeal,
  BasicMaturity,
  DealType,
  DealVersion,
  PreciseDealPricingStatus
} from '../../../types/deal.types';
import { isDefined } from '../../../utils/utils';

const NUM_BARS = 32;
const NUM_MATS = 30;

export interface DealStatusCellValue {
  pricingStatus: PreciseDealPricingStatus;
  dealType: DealType;
  orderPeriod?: any;
  saleDateTime?: any;
  years?: BumpCutData[];
}

export interface BumpCutData {
  year: number;
  bump?: number;
  cut?: number;
  maxYld?: number;
}

export function dealStatusValueGetter(node: {
  data: BasicDeal;
}): DealStatusCellValue | undefined {
  // Handle null or undefined values
  if (!isDefined(node.data)) {
    return undefined;
  }

  const { dealType } = node.data;
  let pricingStatus = getPreciseDealPricingStatus(node.data as DealVersion);

  if (dealType === DealType.negotiated) {
    let orderPeriod: IssueOrderPeriod.OrderPeriodCountdown;
    if (pricingStatus === PreciseDealPricingStatus.Live) {
      orderPeriod = IssueOrderPeriod.getCountdown(node.data as DealVersion);
    }

    if (pricingStatus === PreciseDealPricingStatus.Priced) {
      const currentYear = moment().year();
      if (
        node.data.maturities.length === 0 ||
        node.data.maturities.every((m) => m.yieldSpreadBump === null)
      ) {
        return { pricingStatus, dealType, years: null };
      }

      const years = getYears(currentYear, node);

      return { pricingStatus, dealType, years };
    }
    return { pricingStatus, dealType, orderPeriod };
  }

  let saleDateTime =
    node.data.saleDate &&
    momentTz(node.data.saleDate as TimeZoneMoment).tz('America/New_York');

  if (
    pricingStatus === PreciseDealPricingStatus.Pending ||
    pricingStatus === PreciseDealPricingStatus.Sold
  ) {
    saleDateTime = null;
  }
  // street calendar column to display sold for all closed & show expected for day to day or TBD
  if (dealType === DealType.competitive) {
    if (
      pricingStatus === PreciseDealPricingStatus.Expected &&
      (node.data.isDayToDay || node.data.isTBD)
    ) {
      saleDateTime = null;
    } else if (pricingStatus === PreciseDealPricingStatus.Closed) {
      pricingStatus = PreciseDealPricingStatus.Sold;
    }
    return { pricingStatus, dealType, saleDateTime };
  }

  return { pricingStatus, dealType, saleDateTime };
}

function getYears(currentYear, node) {
  return Array(NUM_BARS)
    .fill(0)
    .map((_value, index) => {
      const year = currentYear + index;
      const dealData = node.data;
      const maturities: BasicMaturity[] = getMaturities(
        index,
        year,
        dealData,
        currentYear
      );

      let maxBump = 0;
      let maxCut = 0;

      maturities.forEach((element) => {
        if (element.yieldSpreadBumpValid === false) {
          maxBump = 0;
          maxCut = 0;
        } else if (element.yieldSpreadBump !== null) {
          if (element.yieldSpreadBump >= 0) {
            maxCut =
              maxCut > element.yieldSpreadBump
                ? maxCut
                : element.yieldSpreadBump;
          } else if (element.yieldSpreadBump < 0) {
            maxBump =
              maxBump > Math.abs(element.yieldSpreadBump)
                ? maxBump
                : Math.abs(element.yieldSpreadBump);
          }
        }
      });

      const res: BumpCutData = getRes(maxCut, maxBump, year);

      return res;
    });
}

function getMaturities(index, year, dealData, currentYear) {
  let maturities;

  if (index > NUM_MATS) {
    maturities = dealData.maturities.filter(
      (m) => m.maturityDate.year() > currentYear + NUM_MATS
    );
  } else {
    maturities = dealData.maturities.filter((m) => {
      return m.maturityDate.year() == year;
    });
  }

  return maturities;
}

function getRes(maxCut, maxBump, year) {
  let res;

  if (maxCut !== 0 && maxBump !== 0) {
    res = {
      year: year,
      bump: -1 * maxBump,
      cut: maxCut
    };
  } else {
    const isMaxBumpGreater = maxBump > maxCut ? -1 * maxBump : maxCut;
    const maxYld = maxBump === 0 ? maxCut : isMaxBumpGreater;

    res = { year: year, maxYld: maxYld };
  }

  return res;
}
